import React, { useState, useEffect } from 'react';
import { Space, Table, Flex, Tooltip, Switch, notification, Button, Input, Select } from 'antd';
import type { TableProps, PaginationProps } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { getXglCollection, updateXgl } from 'src/api/api';
import { useAppSelector } from 'src/redux/hooks';
import { useTranslation } from 'react-i18next';
import { themeSettings } from 'src/configs/index.tsx';
import XglModal from './xglModal';

const currentPageInitialState: API.CurrentPageType = {
    number: 1,
    size: 10,
};

const dimensionInitialState: API.DimensionCollection = {
    items: [],
    total: 0,
    page: 1,
    size: 10,
    pages: 0,
}

const App: React.FC = () => {

    const { t } = useTranslation();
    const accessToken = useAppSelector(state => state.auth.accessToken);
    const clientId = useAppSelector(state => state.client.selectedClientData.id);
    const [xgl, setXgl] = useState<API.DimensionCollection>(dimensionInitialState);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [xglToEdit, setXglToEdit] = useState<API.DimensionItem | null>(null);
    const [updatingXgl, setUpdatingXgl] = useState('');
    const [currentPage, setCurrentPage] = useState<API.CurrentPageType>(
        currentPageInitialState
    );
    const [searchString, setSearchString] = useState<string>('');
    const [orderType, setOrderType] = useState<'ASC' | 'DESC'>('ASC');

  const fetchXgl = async () => {
    setLoading(true);
    try {
      const indexOfLastItem = currentPage.number * currentPage.size;
      const indexOfFirstItem = indexOfLastItem - currentPage.size;

            const params: API.DimensionParams = {
                client_id: clientId,
                limit: currentPage.size,
                offset: indexOfFirstItem,
                search: searchString,
                order_type: orderType,
            }
            const result = await getXglCollection(accessToken, params);
            setXgl(result.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

  useEffect(() => {
    fetchXgl();
  }, [accessToken, clientId, currentPage]);

    const openModal = (xgl?: API.DimensionItem) => {
        setXglToEdit(xgl || null);  // Null if creating a new project
        setIsModalOpen(true);
    };

    const switchXglStatus = async (record: API.DimensionItem) => {
        setUpdatingXgl(record.id!);
        try {
            await updateXgl(accessToken, { ...record, active: !record.active })
            const msg = record.active
                ? `${record.description} ${' '} ${t('is inactivated.')}`
                : `${record.description} ${' '} ${t('is activated.')}`;

      notification['success']({
        message: t('Success'),
        description: msg,
        placement: 'topRight',
        duration: 4.5,
      });

            setXgl((prevXgl: API.DimensionCollection) => {
                const xglItems = prevXgl.items.map((xglItem) => {
                    if (xglItem.id === record.id) {
                        return {
                            ...xglItem,
                            active: !record.active,
                        };
                    }
                    return xglItem;
                });
                return {
                    ...prevXgl,
                    items: [
                        ...xglItems,
                    ],
                };
            });
        }

        catch (error) {
            notification['error']({
                message: t('Error'),
                description: t('Could not change status of xgl'),
                placement: 'topRight',
                duration: 4.5,
            });
            console.error(error);
        } finally {
            setUpdatingXgl('');
        }
    }

  const handleModalClose = () => {
    setIsModalOpen(false);
    setXglToEdit(null);
  };

  const onPageChange: PaginationProps['onChange'] = (page, pageSize) => {
    setCurrentPage({
      number: page,
      size: pageSize,
    });
  };

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
    current,
    pageSize
  ) => {
    setCurrentPage({
      number: current,
      size: pageSize,
    });
  };

    const columns: TableProps<API.DimensionItem>['columns'] = [
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: t('Code'),
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: t('Active'),
            dataIndex: 'active',
            key: 'active',
            render: (active: boolean, record: API.DimensionItem) => (
                <Switch
                    key={record.id + 'isActive'}
                    style={{ backgroundColor: active ? '#52c41a' : 'orange' }}
                    checkedChildren={t('Activated')}
                    unCheckedChildren={t('Inactivated')}
                    value={record.active}
                    loading={record.id === updatingXgl}
                    onChange={() => switchXglStatus(record)}
                />
            ),
        },
        {
            title: t('Action'),
            key: 'action',
            render: (_: any, record: API.DimensionItem) => (
                <Button type="primary" onClick={() => openModal(record)}>
                    {t('Edit')}
                </Button>
            ),
        },
    ];

    return (
        <>
            <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                <Button type="primary" onClick={() => openModal()}>
                    {`${t('Add')} XGL`}
                </Button>
                <Flex wrap gap="small" justify='space-between' >
                    <Space>
                        <Input
                            placeholder={t('Search Code/Description')}
                            style={{ width: 300 }}
                            onChange={(event) => {
                                setSearchString(event.target.value);
                            }}
                            allowClear
                        />
                        <Select
                            defaultValue="ASC"
                            style={{ width: 120 }}
                            onChange={(value: 'ASC' | 'DESC') => setOrderType(value)}
                            options={[
                                { value: 'ASC', label: 'ASC' },
                                { value: 'DESC', label: 'DESC' },
                            ]}
                        />
                        <Button type="primary" onClick={() => onPageChange(1, currentPage.size)}>
                            {t('Filter XGL')}
                        </Button>
                    </Space>

                    <Tooltip title={t('Refresh')} color={themeSettings.token.colorPrimary} key="refresh-company-cards" >
                        <RedoOutlined onClick={fetchXgl} />
                    </Tooltip>
                </Flex>

                <Table<API.DimensionItem>
                    rowKey="id"
                    columns={columns}
                    dataSource={xgl?.items}
                    loading={loading}
                    pagination={{
                        defaultCurrent: currentPageInitialState.number,
                        current: currentPage.number,
                        onChange: onPageChange,
                        defaultPageSize: currentPageInitialState.size,
                        pageSize: currentPage.size,
                        onShowSizeChange: onShowSizeChange,
                        total: xgl?.total ?? 0,
                        showSizeChanger: true,
                    }}
                />
                <XglModal
                    open={isModalOpen}
                    onClose={handleModalClose}
                    xgl={xglToEdit}
                    onSubmit={fetchXgl}  // Refetch xgl on submit
                />
            </Space>
        </>
    )
}

export default App;
